import { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Camera,
  Aperture,
  Lightning,
  Backpack,
  Plus,
  Trash,
  FloppyDisk,
  Package,
  CaretDown,
  DownloadSimple,
  FilmStrip,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { FILM_CAMERAS } from "@/data/filmCameras";

const API = "/api";

const EMPTY_DETAILS = {
  model: "",
  serial_number: "",
  quantity: 1,
  purchase_date: "",
  purchase_price: "",
  condition: "",
  location: "",
  remarks: "",
};

const SUPPORTED_CAMERAS = [
  { id: "canon_eos_rebel_t3i", labelKey: "cam_canon_eos_rebel_t3i" },
  { id: "canon_eos_rebel_t7", labelKey: "cam_canon_eos_rebel_t7" },
  { id: "canon_eos_rebel_t8i", labelKey: "cam_canon_eos_rebel_t8i" },
  { id: "canon_eos_20d", labelKey: "cam_canon_eos_20d" },
  { id: "canon_eos_80d", labelKey: "cam_canon_eos_80d" },
  { id: "canon_eos_90d", labelKey: "cam_canon_eos_90d" },
  { id: "canon_eos_250d", labelKey: "cam_canon_eos_250d" },
  { id: "canon_eos_4000d", labelKey: "cam_canon_eos_4000d" },
  { id: "canon_eos_5d_mark_iii", labelKey: "cam_canon_eos_5d_mark_iii" },
  { id: "canon_eos_5d_mark_iv", labelKey: "cam_canon_eos_5d_mark_iv" },
  { id: "canon_eos_6d_mark_ii", labelKey: "cam_canon_eos_6d_mark_ii" },
  { id: "canon_eos_1dx_mark_iii", labelKey: "cam_canon_eos_1dx_mark_iii" },
  { id: "canon_eos_r", labelKey: "cam_canon_eos_r" },
  { id: "canon_eos_rp", labelKey: "cam_canon_eos_rp" },
  { id: "canon_eos_r3", labelKey: "cam_canon_eos_r3" },
  { id: "canon_eos_r5", labelKey: "cam_canon_eos_r5" },
  { id: "canon_eos_r6", labelKey: "cam_canon_eos_r6" },
  { id: "canon_eos_r7", labelKey: "cam_canon_eos_r7" },
  { id: "canon_eos_r8", labelKey: "cam_canon_eos_r8" },
  { id: "canon_eos_r10", labelKey: "cam_canon_eos_r10" },
  { id: "canon_eos_r50", labelKey: "cam_canon_eos_r50" },
  { id: "canon_eos_r100", labelKey: "cam_canon_eos_r100" },
  { id: "nikon_d3200", labelKey: "cam_nikon_d3200" },
  { id: "nikon_d3500", labelKey: "cam_nikon_d3500" },
  { id: "nikon_d5600", labelKey: "cam_nikon_d5600" },
  { id: "nikon_d7500", labelKey: "cam_nikon_d7500" },
  { id: "nikon_d750", labelKey: "cam_nikon_d750" },
  { id: "nikon_d780", labelKey: "cam_nikon_d780" },
  { id: "nikon_d850", labelKey: "cam_nikon_d850" },
  { id: "nikon_d6", labelKey: "cam_nikon_d6" },
  { id: "nikon_coolpix_p100", labelKey: "cam_nikon_coolpix_p100" },
  { id: "nikon_z9", labelKey: "cam_nikon_z9" },
  { id: "nikon_z8", labelKey: "cam_nikon_z8" },
  { id: "nikon_z7ii", labelKey: "cam_nikon_z7ii" },
  { id: "nikon_z6iii", labelKey: "cam_nikon_z6iii" },
  { id: "nikon_z5", labelKey: "cam_nikon_z5" },
  { id: "nikon_zf", labelKey: "cam_nikon_zf" },
  { id: "nikon_z50", labelKey: "cam_nikon_z50" },
  { id: "nikon_zfc", labelKey: "cam_nikon_zfc" },
  { id: "nikon_z30", labelKey: "cam_nikon_z30" },
  { id: "pentax_k70", labelKey: "cam_pentax_k70" },
  { id: "pentax_kf", labelKey: "cam_pentax_kf" },
  { id: "sony_a58", labelKey: "cam_sony_a58" },
  { id: "sony_a68", labelKey: "cam_sony_a68" },
  { id: "sony_a77_ii", labelKey: "cam_sony_a77_ii" },
  { id: "sony_a99ii", labelKey: "cam_sony_a99ii" },
  { id: "sony_a1", labelKey: "cam_sony_a1" },
  { id: "sony_a9iii", labelKey: "cam_sony_a9iii" },
  { id: "sony_a7rv", labelKey: "cam_sony_a7rv" },
  { id: "sony_a7iv", labelKey: "cam_sony_a7iv" },
  { id: "sony_a7siii", labelKey: "cam_sony_a7siii" },
  { id: "sony_a7cii", labelKey: "cam_sony_a7cii" },
  { id: "sony_zve1", labelKey: "cam_sony_zve1" },
  { id: "sony_a6700", labelKey: "cam_sony_a6700" },
  { id: "sony_a6400", labelKey: "cam_sony_a6400" },
  { id: "sony_a6100", labelKey: "cam_sony_a6100" },
  { id: "sony_zve10", labelKey: "cam_sony_zve10" },
  { id: "sony_fx3", labelKey: "cam_sony_fx3" },
  { id: "sony_fx30", labelKey: "cam_sony_fx30" },
  { id: "fujifilm_x100vi", labelKey: "cam_fujifilm_x100vi" },
  { id: "fujifilm_xt5", labelKey: "cam_fujifilm_xt5" },
  { id: "fujifilm_xs20", labelKey: "cam_fujifilm_xs20" },
  { id: "fujifilm_xt30ii", labelKey: "cam_fujifilm_xt30ii" },
  { id: "fujifilm_xh2s", labelKey: "cam_fujifilm_xh2s" },
  { id: "fujifilm_xm5", labelKey: "cam_fujifilm_xm5" },
  { id: "fujifilm_xpro3", labelKey: "cam_fujifilm_xpro3" },
  { id: "fujifilm_gfx100s", labelKey: "cam_fujifilm_gfx100s" },
  { id: "fujifilm_xt4", labelKey: "cam_fujifilm_xt4" },
  { id: "fujifilm_xt3", labelKey: "cam_fujifilm_xt3" },
  { id: "fujifilm_xt2", labelKey: "cam_fujifilm_xt2" },
  { id: "fujifilm_xh1", labelKey: "cam_fujifilm_xh1" },
  { id: "fujifilm_xh2", labelKey: "cam_fujifilm_xh2" },
  { id: "fujifilm_xs10", labelKey: "cam_fujifilm_xs10" },
  { id: "fujifilm_xt50", labelKey: "cam_fujifilm_xt50" },
  { id: "fujifilm_xe4", labelKey: "cam_fujifilm_xe4" },
  { id: "fujifilm_xa7", labelKey: "cam_fujifilm_xa7" },
  { id: "fujifilm_gfx100ii", labelKey: "cam_fujifilm_gfx100ii" },
  { id: "fujifilm_gfx50sii", labelKey: "cam_fujifilm_gfx50sii" },
  { id: "fujifilm_s5pro", labelKey: "cam_fujifilm_s5pro" },
  { id: "fujifilm_s3pro", labelKey: "cam_fujifilm_s3pro" },
];

const LIGHTING_OPTIONS = [
  { id: "speedlight", labelKey: "gear_speedlight" },
  { id: "led_panel", labelKey: "gear_led_panel" },
  { id: "strobe", labelKey: "gear_strobe" },
  { id: "light_meter", labelKey: "gear_light_meter" },
  { id: "color_checker", labelKey: "gear_color_checker" },
];

const SUPPORT_OPTIONS = [
  { id: "tripod", labelKey: "gear_tripod" },
  { id: "monopod", labelKey: "gear_monopod" },
  { id: "reflector", labelKey: "gear_reflector" },
];

const LENS_TYPES = [
  { value: "standard", labelKey: "lens_standard" },
  { value: "wide", labelKey: "lens_wide" },
  { value: "telephoto", labelKey: "lens_telephoto" },
  { value: "macro", labelKey: "lens_macro" },
];

const CONDITIONS = ["New", "Excellent", "Good", "Fair", "Poor"];

function migrateGearData(data) {
  return {
    cameras: (data.cameras || []).map((c) =>
      typeof c === "string"
        ? { camera_id: c, ...EMPTY_DETAILS }
        : { ...EMPTY_DETAILS, ...c }
    ),
    lenses: (data.lenses || []).map((l) => ({ ...EMPTY_DETAILS, ...l })),
    lighting: (data.lighting || []).map((l) =>
      typeof l === "string"
        ? { item_id: l, ...EMPTY_DETAILS }
        : { ...EMPTY_DETAILS, ...l }
    ),
    support: (data.support || []).map((s) =>
      typeof s === "string"
        ? { item_id: s, ...EMPTY_DETAILS }
        : { ...EMPTY_DETAILS, ...s }
    ),
    accessories: (data.accessories || []).map((a) =>
      typeof a === "string"
        ? { name: a, ...EMPTY_DETAILS }
        : { ...EMPTY_DETAILS, ...a }
    ),
  };
}

function DetailsFields({ item, onChange, t }) {
  const update = (field, val) => onChange({ ...item, [field]: val });
  return (
    <div className="grid grid-cols-2 gap-3 px-4 pb-4 border-t border-white/5 pt-3">
      <div>
        <Label className="text-[#52525B] text-xs">{t("inv_model")}</Label>
        <Input value={item.model || ""} onChange={(e) => update("model", e.target.value)} className="bg-[#0A0A0A] border-white/10 text-white text-sm h-8" />
      </div>
      <div>
        <Label className="text-[#52525B] text-xs">{t("inv_serial")}</Label>
        <Input value={item.serial_number || ""} onChange={(e) => update("serial_number", e.target.value)} className="bg-[#0A0A0A] border-white/10 text-white text-sm h-8" />
      </div>
      <div>
        <Label className="text-[#52525B] text-xs">{t("inv_quantity")}</Label>
        <Input type="number" min="1" value={item.quantity || 1} onChange={(e) => update("quantity", parseInt(e.target.value) || 1)} className="bg-[#0A0A0A] border-white/10 text-white text-sm h-8" />
      </div>
      <div>
        <Label className="text-[#52525B] text-xs">{t("inv_condition")}</Label>
        <Select value={item.condition || ""} onValueChange={(v) => update("condition", v)}>
          <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white text-sm h-8">
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent className="bg-[#121214] border-white/10">
            {CONDITIONS.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-[#52525B] text-xs">{t("inv_location")}</Label>
        <Input value={item.location || ""} onChange={(e) => update("location", e.target.value)} className="bg-[#0A0A0A] border-white/10 text-white text-sm h-8" />
      </div>
      <div>
        <Label className="text-[#52525B] text-xs">{t("inv_purchase_date")}</Label>
        <Input type="date" value={item.purchase_date || ""} onChange={(e) => update("purchase_date", e.target.value)} className="bg-[#0A0A0A] border-white/10 text-white text-sm h-8" />
      </div>
      <div>
        <Label className="text-[#52525B] text-xs">{t("inv_purchase_price")}</Label>
        <Input type="number" step="0.01" min="0" placeholder="0.00" value={item.purchase_price || ""} onChange={(e) => update("purchase_price", e.target.value)} className="bg-[#0A0A0A] border-white/10 text-white text-sm h-8" />
      </div>
      <div className="col-span-2">
        <Label className="text-[#52525B] text-xs">{t("inv_remarks")}</Label>
        <Input value={item.remarks || ""} onChange={(e) => update("remarks", e.target.value)} className="bg-[#0A0A0A] border-white/10 text-white text-sm h-8" placeholder={t("inv_remarks_placeholder")} />
      </div>
    </div>
  );
}

function GearItemRow({ item, label, onUpdate, onRemove, subtitle, testId, t }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-[#0A0A0A] border border-white/10 rounded-lg" data-testid={testId}>
      <div className="flex items-center justify-between px-4 py-3">
        <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-2 flex-1 text-left">
          <CaretDown weight="bold" className={cn("w-3 h-3 text-[#52525B] transition-transform", open && "rotate-180")} />
          <span className="text-white text-sm font-medium">{label}</span>
          {subtitle && <span className="text-[#71717A] text-xs">{subtitle}</span>}
        </button>
        <button type="button" onClick={onRemove} className="text-[#52525B] hover:text-red-400 transition-colors p-1">
          <Trash weight="regular" className="w-4 h-4" />
        </button>
      </div>
      {open && <DetailsFields item={item} onChange={onUpdate} t={t} />}
    </div>
  );
}

export default function GearPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [gear, setGear] = useState({ cameras: [], lenses: [], lighting: [], support: [], accessories: [] });
  const [saving, setSaving] = useState(false);

  const [lensName, setLensName] = useState("");
  const [focalMin, setFocalMin] = useState("");
  const [focalMax, setFocalMax] = useState("");
  const [lensAperture, setLensAperture] = useState("");
  const [lensType, setLensType] = useState("standard");
  const [accName, setAccName] = useState("");

  useEffect(() => {
    if (user && user !== false) {
      axios.get(`${API}/gear`, { withCredentials: true }).then((res) => {
        setGear(migrateGearData(res.data));
      }).catch(() => {});
    }
  }, [user]);

  if (!user || user === false) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <Camera weight="thin" className="w-16 h-16 text-[#242429] mx-auto mb-4" />
        <h2 className="text-white text-xl font-['Outfit'] mb-2">{t("gear_title")}</h2>
        <p className="text-[#71717A]">{t("gear_login_required")}</p>
      </div>
    );
  }

  // Persist the gear object immediately (used for structural add/remove/toggle
  // so users don't lose items by forgetting to press Save).
  const commitGear = async (newGear, successMsg) => {
    const prev = gear;
    setGear(newGear);
    try {
      await axios.post(`${API}/gear`, newGear, { withCredentials: true });
      if (successMsg) toast.success(successMsg);
    } catch {
      toast.error(t("error"));
      // Re-sync from server so the UI never shows unpersisted items.
      try {
        const res = await axios.get(`${API}/gear`, { withCredentials: true });
        setGear(migrateGearData(res.data));
      } catch {
        setGear(prev);
      }
    }
  };

  // Camera toggle
  const toggleCamera = (camId) => {
    const exists = gear.cameras.find((c) => c.camera_id === camId);
    if (exists) {
      commitGear({ ...gear, cameras: gear.cameras.filter((c) => c.camera_id !== camId) });
    } else {
      commitGear({ ...gear, cameras: [...gear.cameras, { camera_id: camId, ...EMPTY_DETAILS }] });
    }
  };

  // Lighting/support toggle
  const toggleItem = (category, itemId) => {
    const list = gear[category];
    const exists = list.find((x) => x.item_id === itemId);
    if (exists) {
      commitGear({ ...gear, [category]: list.filter((x) => x.item_id !== itemId) });
    } else {
      commitGear({ ...gear, [category]: [...list, { item_id: itemId, ...EMPTY_DETAILS }] });
    }
  };

  const updateItem = (category, index, updated) => {
    const list = [...gear[category]];
    list[index] = updated;
    setGear({ ...gear, [category]: list });
  };

  const removeItem = (category, index) => {
    commitGear({ ...gear, [category]: gear[category].filter((_, i) => i !== index) });
  };

  const addLens = () => {
    if (!lensName.trim() || !focalMin || !lensAperture) return;
    const fm = parseFloat(focalMin);
    const fmx = focalMax ? parseFloat(focalMax) : fm;
    commitGear({
      ...gear,
      lenses: [...gear.lenses, { name: lensName.trim(), focal_min: fm, focal_max: fmx, max_aperture: parseFloat(lensAperture), lens_type: lensType, ...EMPTY_DETAILS }],
    }, t("gear_saved"));
    setLensName(""); setFocalMin(""); setFocalMax(""); setLensAperture(""); setLensType("standard");
  };

  const addAccessory = () => {
    if (!accName.trim()) return;
    commitGear({ ...gear, accessories: [...gear.accessories, { name: accName.trim(), ...EMPTY_DETAILS }] }, t("gear_saved"));
    setAccName("");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/gear`, gear, { withCredentials: true });
      toast.success(t("gear_saved"));
    } catch { toast.error(t("error")); }
    setSaving(false);
  };

  const handleDownload = async () => {
    // Auto-save first, then reload fresh data for export
    try {
      await axios.post(`${API}/gear`, gear, { withCredentials: true });
      const res = await axios.get(`${API}/gear`, { withCredentials: true });
      const freshGear = res.data;
      generateExcel(freshGear);
    } catch {
      // If save/reload fails, use local state
      generateExcel(gear);
    }
  };

  const generateExcel = (gearData) => {
    const rows = [];
    const today = new Date().toLocaleDateString();

    gearData.cameras.forEach((c) => {
      const camId = c.camera_id || c;
      const cam = SUPPORTED_CAMERAS.find((sc) => sc.id === camId);
      const film = !cam ? FILM_CAMERAS.find((fc) => fc.id === camId) : null;
      const name = cam ? t(cam.labelKey) : film ? film.label : camId;
      rows.push({ Category: film ? "Film Camera" : "Camera", Name: name, Model: c.model || "", "Serial Number": c.serial_number || "", Quantity: c.quantity || 1, "Inventory Date": today, "Purchase Date": c.purchase_date || "", "Purchase Price": c.purchase_price || "", Condition: c.condition || "", "Current Location": c.location || "", Remarks: c.remarks || "" });
    });

    gearData.lenses.forEach((l) => {
      const foc = l.focal_min === l.focal_max ? `${l.focal_min}mm` : `${l.focal_min}-${l.focal_max}mm`;
      rows.push({ Category: "Lens", Name: l.name, Model: l.model || `${foc} f/${l.max_aperture}`, "Serial Number": l.serial_number || "", Quantity: l.quantity || 1, "Inventory Date": today, "Purchase Date": l.purchase_date || "", "Purchase Price": l.purchase_price || "", Condition: l.condition || "", "Current Location": l.location || "", Remarks: l.remarks || "" });
    });

    gearData.lighting.forEach((l) => {
      const lit = LIGHTING_OPTIONS.find((lo) => lo.id === (l.item_id || l));
      rows.push({ Category: "Lighting", Name: lit ? t(lit.labelKey) : (l.item_id || l), Model: l.model || "", "Serial Number": l.serial_number || "", Quantity: l.quantity || 1, "Inventory Date": today, "Purchase Date": l.purchase_date || "", "Purchase Price": l.purchase_price || "", Condition: l.condition || "", "Current Location": l.location || "", Remarks: l.remarks || "" });
    });

    gearData.support.forEach((s) => {
      const sup = SUPPORT_OPTIONS.find((so) => so.id === (s.item_id || s));
      rows.push({ Category: "Support Gear", Name: sup ? t(sup.labelKey) : (s.item_id || s), Model: s.model || "", "Serial Number": s.serial_number || "", Quantity: s.quantity || 1, "Inventory Date": today, "Purchase Date": s.purchase_date || "", "Purchase Price": s.purchase_price || "", Condition: s.condition || "", "Current Location": s.location || "", Remarks: s.remarks || "" });
    });

    gearData.accessories.forEach((a) => {
      rows.push({ Category: "Accessory", Name: a.name, Model: a.model || "", "Serial Number": a.serial_number || "", Quantity: a.quantity || 1, "Inventory Date": today, "Purchase Date": a.purchase_date || "", "Purchase Price": a.purchase_price || "", Condition: a.condition || "", "Current Location": a.location || "", Remarks: a.remarks || "" });
    });

    if (rows.length === 0) { toast.error(t("inv_no_items")); return; }

    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [{ wch: 14 }, { wch: 28 }, { wch: 20 }, { wch: 16 }, { wch: 8 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 18 }, { wch: 30 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Gear Inventory");
    XLSX.writeFile(wb, "DSLR_Gear_Inventory.xlsx");
    toast.success(t("inv_downloaded"));
  };

  const selectContentClass = "bg-[#121214] border-white/10";

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 lg:py-8" data-testid="gear-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-semibold text-white mb-1">{t("gear_title")}</h1>
          <p className="text-[#71717A] text-sm">{t("gear_subtitle")}</p>
        </div>
        <Button onClick={handleDownload} variant="outline" className="border-white/10 text-white hover:bg-white/5 shrink-0" data-testid="gear-download-btn">
          <DownloadSimple weight="bold" className="w-4 h-4 mr-1.5" />
          {t("inv_download")}
        </Button>
      </div>

      <div className="space-y-5">
        {/* Cameras */}
        <div className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-5 space-y-3">
          <h3 className="text-white font-['Outfit'] font-medium flex items-center gap-2 text-sm">
            <Camera weight="regular" className="text-[#F5A524] w-4 h-4" />
            {t("gear_cameras")}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {SUPPORTED_CAMERAS.map((cam) => {
              const active = gear.cameras.some((c) => c.camera_id === cam.id);
              return (
                <button key={cam.id} type="button" onClick={() => toggleCamera(cam.id)} className={cn("flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-all text-sm", active ? "bg-[#F5A524]/10 border-[#F5A524]/40 text-white" : "bg-[#0A0A0A] border-white/10 text-[#A1A1AA] hover:border-white/20")} data-testid={`gear-cam-${cam.id}`}>
                  <div className={cn("w-4 h-4 rounded border-2 flex items-center justify-center shrink-0", active ? "bg-[#F5A524] border-[#F5A524]" : "border-[#52525B]")}>
                    {active && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="#0A0A0A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                  </div>
                  {t(cam.labelKey)}
                </button>
              );
            })}
          </div>
          {gear.cameras.map((cam, i) => {
            if (String(cam.camera_id).startsWith("film_")) return null;
            const info = SUPPORTED_CAMERAS.find((sc) => sc.id === cam.camera_id);
            return (
              <GearItemRow key={cam.camera_id} item={cam} label={info ? t(info.labelKey) : cam.camera_id} onUpdate={(u) => updateItem("cameras", i, u)} onRemove={() => toggleCamera(cam.camera_id)} testId={`gear-cam-detail-${i}`} t={t} />
            );
          })}
        </div>

        {/* Film Cameras */}
        <div className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-5 space-y-3">
          <h3 className="text-white font-['Outfit'] font-medium flex items-center gap-2 text-sm">
            <FilmStrip weight="regular" className="text-[#F5A524] w-4 h-4" />
            {t("gear_film_cameras")}
          </h3>
          <p className="text-[#52525B] text-xs">{t("gear_film_cameras_hint")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {FILM_CAMERAS.map((cam) => {
              const active = gear.cameras.some((c) => c.camera_id === cam.id);
              return (
                <button key={cam.id} type="button" onClick={() => toggleCamera(cam.id)} className={cn("flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-all text-sm", active ? "bg-[#F5A524]/10 border-[#F5A524]/40 text-white" : "bg-[#0A0A0A] border-white/10 text-[#A1A1AA] hover:border-white/20")} data-testid={`gear-film-cam-${cam.id}`}>
                  <div className={cn("w-4 h-4 rounded border-2 flex items-center justify-center shrink-0", active ? "bg-[#F5A524] border-[#F5A524]" : "border-[#52525B]")}>
                    {active && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="#0A0A0A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                  </div>
                  {cam.label}
                </button>
              );
            })}
          </div>
          {gear.cameras.map((cam, i) => {
            if (!String(cam.camera_id).startsWith("film_")) return null;
            const info = FILM_CAMERAS.find((fc) => fc.id === cam.camera_id);
            return (
              <GearItemRow key={cam.camera_id} item={cam} label={info ? info.label : cam.camera_id} onUpdate={(u) => updateItem("cameras", i, u)} onRemove={() => toggleCamera(cam.camera_id)} testId={`gear-film-detail-${i}`} t={t} />
            );
          })}
        </div>

        {/* Lenses */}
        <div className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-5 space-y-3">
          <h3 className="text-white font-['Outfit'] font-medium flex items-center gap-2 text-sm">
            <Aperture weight="regular" className="text-[#F5A524] w-4 h-4" />
            {t("gear_lenses")}
          </h3>
          {gear.lenses.length === 0 && <p className="text-[#52525B] text-sm italic">{t("gear_no_lenses")}</p>}
          {gear.lenses.map((lens, i) => (
            <GearItemRow key={i} item={lens} label={lens.name} subtitle={`${lens.focal_min === lens.focal_max ? `${lens.focal_min}mm` : `${lens.focal_min}-${lens.focal_max}mm`} f/${lens.max_aperture}`} onUpdate={(u) => updateItem("lenses", i, u)} onRemove={() => removeItem("lenses", i)} testId={`gear-lens-${i}`} t={t} />
          ))}
          <div className="border border-dashed border-white/10 rounded-lg p-4 space-y-3">
            <Label className="text-[#71717A] text-xs uppercase tracking-[0.15em]">{t("gear_add_lens")}</Label>
            <Input placeholder={t("gear_lens_name")} value={lensName} onChange={(e) => setLensName(e.target.value)} className="bg-[#0A0A0A] border-white/10 text-white text-sm" data-testid="gear-lens-name-input" />
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-[#52525B] text-xs">{t("gear_focal_min")} <span className="text-[#F5A524]">*</span></Label>
                <Input placeholder="50" value={focalMin} onChange={(e) => setFocalMin(e.target.value.replace(/[^0-9.]/g, ''))} className="bg-[#0A0A0A] border-white/10 text-white text-sm" data-testid="gear-focal-min-input" />
              </div>
              <div>
                <Label className="text-[#52525B] text-xs">{t("gear_focal_max")}</Label>
                <Input placeholder={t("gear_optional")} value={focalMax} onChange={(e) => setFocalMax(e.target.value.replace(/[^0-9.]/g, ''))} className="bg-[#0A0A0A] border-white/10 text-white text-sm" data-testid="gear-focal-max-input" />
              </div>
              <div>
                <Label className="text-[#52525B] text-xs">{t("gear_max_aperture")} <span className="text-[#F5A524]">*</span></Label>
                <Input placeholder="1.8" value={lensAperture} onChange={(e) => setLensAperture(e.target.value.replace(/[^0-9.]/g, ''))} className="bg-[#0A0A0A] border-white/10 text-white text-sm" data-testid="gear-lens-aperture-input" />
              </div>
            </div>
            <div className="flex gap-2">
              <Select value={lensType} onValueChange={setLensType}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white text-sm flex-1" data-testid="gear-lens-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className={selectContentClass}>
                  {LENS_TYPES.map((lt) => (<SelectItem key={lt.value} value={lt.value}>{t(lt.labelKey)}</SelectItem>))}
                </SelectContent>
              </Select>
              <Button type="button" onClick={addLens} disabled={!lensName.trim() || !focalMin || !lensAperture} className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium shrink-0" data-testid="gear-add-lens-btn">
                <Plus weight="bold" className="w-4 h-4 mr-1" />{t("gear_add_lens")}
              </Button>
            </div>
          </div>
        </div>

        {/* Lighting */}
        <div className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-5 space-y-3">
          <h3 className="text-white font-['Outfit'] font-medium flex items-center gap-2 text-sm">
            <Lightning weight="regular" className="text-[#F5A524] w-4 h-4" />
            {t("gear_lighting")}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {LIGHTING_OPTIONS.map((item) => {
              const active = gear.lighting.some((l) => l.item_id === item.id);
              return (
                <button key={item.id} type="button" onClick={() => toggleItem("lighting", item.id)} className={cn("flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-all text-sm", active ? "bg-[#F5A524]/10 border-[#F5A524]/40 text-white" : "bg-[#0A0A0A] border-white/10 text-[#A1A1AA] hover:border-white/20")} data-testid={`gear-light-${item.id}`}>
                  <div className={cn("w-4 h-4 rounded border-2 flex items-center justify-center shrink-0", active ? "bg-[#F5A524] border-[#F5A524]" : "border-[#52525B]")}>
                    {active && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="#0A0A0A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                  </div>
                  {t(item.labelKey)}
                </button>
              );
            })}
          </div>
          {gear.lighting.map((item, i) => {
            const info = LIGHTING_OPTIONS.find((lo) => lo.id === item.item_id);
            return <GearItemRow key={item.item_id} item={item} label={info ? t(info.labelKey) : item.item_id} onUpdate={(u) => updateItem("lighting", i, u)} onRemove={() => toggleItem("lighting", item.item_id)} testId={`gear-light-detail-${i}`} t={t} />;
          })}
        </div>

        {/* Support Gear */}
        <div className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-5 space-y-3">
          <h3 className="text-white font-['Outfit'] font-medium flex items-center gap-2 text-sm">
            <Backpack weight="regular" className="text-[#F5A524] w-4 h-4" />
            {t("gear_support")}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {SUPPORT_OPTIONS.map((item) => {
              const active = gear.support.some((s) => s.item_id === item.id);
              return (
                <button key={item.id} type="button" onClick={() => toggleItem("support", item.id)} className={cn("flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-all text-sm", active ? "bg-[#F5A524]/10 border-[#F5A524]/40 text-white" : "bg-[#0A0A0A] border-white/10 text-[#A1A1AA] hover:border-white/20")} data-testid={`gear-support-${item.id}`}>
                  <div className={cn("w-4 h-4 rounded border-2 flex items-center justify-center shrink-0", active ? "bg-[#F5A524] border-[#F5A524]" : "border-[#52525B]")}>
                    {active && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="#0A0A0A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                  </div>
                  {t(item.labelKey)}
                </button>
              );
            })}
          </div>
          {gear.support.map((item, i) => {
            const info = SUPPORT_OPTIONS.find((so) => so.id === item.item_id);
            return <GearItemRow key={item.item_id} item={item} label={info ? t(info.labelKey) : item.item_id} onUpdate={(u) => updateItem("support", i, u)} onRemove={() => toggleItem("support", item.item_id)} testId={`gear-support-detail-${i}`} t={t} />;
          })}
        </div>

        {/* Accessories */}
        <div className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-5 space-y-3">
          <h3 className="text-white font-['Outfit'] font-medium flex items-center gap-2 text-sm">
            <Package weight="regular" className="text-[#F5A524] w-4 h-4" />
            {t("gear_accessories")}
          </h3>
          <p className="text-[#52525B] text-xs">{t("gear_accessories_hint")}</p>
          {gear.accessories.map((item, i) => (
            <GearItemRow key={i} item={item} label={item.name} onUpdate={(u) => updateItem("accessories", i, u)} onRemove={() => removeItem("accessories", i)} testId={`gear-accessory-${i}`} t={t} />
          ))}
          <div className="flex gap-2">
            <Input placeholder={t("gear_accessory_placeholder")} value={accName} onChange={(e) => setAccName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addAccessory()} className="bg-[#0A0A0A] border-white/10 text-white text-sm flex-1" data-testid="gear-accessory-input" />
            <Button type="button" onClick={addAccessory} disabled={!accName.trim()} className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium shrink-0" data-testid="gear-add-accessory-btn">
              <Plus weight="bold" className="w-4 h-4 mr-1" />{t("gear_add")}
            </Button>
          </div>
        </div>

        {/* Save */}
        <Button onClick={handleSave} disabled={saving} className="w-full h-12 bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-semibold text-base active:scale-[0.98] transition-all" data-testid="gear-save-btn">
          <FloppyDisk weight="regular" className="w-4 h-4 mr-2" />
          {saving ? t("loading") : t("gear_save")}
        </Button>
      </div>
    </div>
  );
}
